import React from 'react';
import { TrendingUp, Clock, Users, ArrowRight, CheckCircle, Star } from 'lucide-react';
import useMobile from '../hooks/useMobile';

const HomePage = ({ setCurrentPage }) => {
  const isMobile = useMobile();
  
  return (
    <div className="fade-in">
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
        color: 'white',
        padding: isMobile ? '60px 0' : '120px 0',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div className="container">
          <h1 style={{ 
            fontSize: isMobile ? '2.5rem' : '4rem', 
            marginBottom: '20px',
            fontWeight: 'bold'
          }}>
            Your Restaurant.<br />Your App.<br />More Sales.
          </h1>
          <p style={{ 
            fontSize: isMobile ? '1.2rem' : '1.5rem', 
            marginBottom: '40px', 
            maxWidth: '700px', 
            margin: '0 auto 40px',
            opacity: 0.95
          }}>
            Give your customers their own mobile app to order directly from you. 
            No commissions. No complicated fees. Just more revenue.
          </p>
          <div style={{
            display: 'flex',
            gap: '20px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button 
              onClick={() => setCurrentPage('products')}
              style={{
                backgroundColor: 'white',
                color: '#7c3aed',
                border: 'none',
                padding: '18px 40px',
                borderRadius: '8px',
                fontSize: '1.2rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}
            >
              See How It Works <ArrowRight size={20} />
            </button>
            <button 
              onClick={() => setCurrentPage('support')}
              style={{
                backgroundColor: 'transparent',
                color: 'white',
                border: '2px solid white',
                padding: '16px 40px',
                borderRadius: '8px',
                fontSize: '1.2rem',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Get Started
            </button>
          </div>
          <p style={{
            marginTop: '30px',
            fontSize: '1.1rem',
            opacity: 0.9
          }}>
            ✨ No setup fees • No monthly fees • Pay only when you make money
          </p>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section style={{ padding: '80px 0', backgroundColor: '#fff' }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '60px',
            alignItems: 'center'
          }}>
            <div>
              <h2 style={{ 
                fontSize: isMobile ? '2rem' : '2.5rem', 
                marginBottom: '30px',
                color: '#1a1a1a'
              }}>
                Stop Losing Money to Third-Party Apps
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {[
                  "Third-party apps charge 15-30% commission on every order",
                  "You don't own your customer relationships",
                  "Limited control over your brand experience",
                  "Competing with every other restaurant on the platform"
                ].map((problem, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '15px'
                  }}>
                    <span style={{ 
                      color: '#ef4444', 
                      fontSize: '1.5rem',
                      lineHeight: '1'
                    }}>✗</span>
                    <p style={{ 
                      color: '#4b5563', 
                      fontSize: '1.1rem',
                      margin: 0,
                      fontWeight: '500'
                    }}>{problem}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h2 style={{ 
                fontSize: isMobile ? '2rem' : '2.5rem', 
                marginBottom: '30px',
                color: '#1a1a1a'
              }}>
                Take Back Control with Your Own App
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {[
                  "Keep 95% of your revenue (only 5% platform fee)",
                  "Build direct relationships with your customers",
                  "Full control over your brand and menu",
                  "Your customers order directly from you"
                ].map((solution, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '15px'
                  }}>
                    <CheckCircle size={24} color="#10b981" style={{ flexShrink: 0 }} />
                    <p style={{ 
                      color: '#1a1a1a', 
                      fontSize: '1.1rem',
                      margin: 0,
                      fontWeight: '500'
                    }}>{solution}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section style={{ 
        padding: '80px 0', 
        backgroundColor: '#f9fafb' 
      }}>
        <div className="container">
          <h2 style={{ 
            textAlign: 'center', 
            fontSize: isMobile ? '2rem' : '3rem', 
            marginBottom: '20px' 
          }}>
            Everything You Need to Succeed
          </h2>
          <p style={{
            textAlign: 'center',
            fontSize: '1.3rem',
            color: '#6b7280',
            marginBottom: '60px',
            maxWidth: '700px',
            margin: '0 auto 60px'
          }}>
            A complete solution designed specifically for restaurants like yours
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '40px'
          }}>
            {[
              {
                icon: <TrendingUp size={48} color="#7c3aed" />,
                title: "Increase Revenue",
                description: "Customers spend 23% more when ordering through branded apps vs third-party platforms"
              },
              {
                icon: <Users size={48} color="#7c3aed" />,
                title: "Build Loyalty",
                description: "Reward your best customers and keep them coming back with built-in loyalty programs"
              },
              {
                icon: <Clock size={48} color="#7c3aed" />,
                title: "Save Time",
                description: "Manage everything from one dashboard - menu, orders, customers, and marketing"
              }
            ].map((feature, idx) => (
              <div key={idx} className="card" style={{
                padding: '40px',
                backgroundColor: 'white',
                borderRadius: '12px',
                textAlign: 'center',
                boxShadow: '0 5px 20px rgba(0,0,0,0.08)'
              }}>
                <div style={{ marginBottom: '20px' }}>{feature.icon}</div>
                <h3 style={{ fontSize: '1.5rem', marginBottom: '15px' }}>{feature.title}</h3>
                <p style={{ color: '#6b7280', lineHeight: '1.6' }}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section style={{
        background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
        padding: '80px 0'
      }}>
        <div className="container">
          <h2 style={{ 
            textAlign: 'center', 
            fontSize: isMobile ? '2rem' : '2.5rem', 
            marginBottom: '60px' 
          }}>
            Restaurants Love Krafty
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '30px'
          }}>
            {[
              {
                name: "Pizza Palace",
                review: "Our sales increased by 40% in the first 3 months. The app paid for itself in the first week!",
                owner: "Tony M."
              },
              {
                name: "Sakura Sushi",
                review: "Finally, we own our customer relationships. The loyalty program has been a game-changer.",
                owner: "Yuki S."
              },
              {
                name: "Burger Barn",
                review: "Setup was incredibly easy. We were taking orders through our own app within days.",
                owner: "Mike R."
              }
            ].map((testimonial, idx) => (
              <div key={idx} style={{
                backgroundColor: 'white',
                padding: '30px',
                borderRadius: '12px',
                boxShadow: '0 3px 10px rgba(0,0,0,0.08)'
              }}>
                <div style={{ 
                  display: 'flex', 
                  marginBottom: '15px',
                  color: '#fbbf24'
                }}>
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={20} fill="currentColor" />
                  ))}
                </div>
                <p style={{ 
                  fontSize: '1.1rem', 
                  marginBottom: '20px',
                  color: '#4b5563',
                  fontStyle: 'italic'
                }}>
                  "{testimonial.review}"
                </p>
                <div>
                  <p style={{ fontWeight: 'bold', margin: 0 }}>{testimonial.name}</p>
                  <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>{testimonial.owner}, Owner</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section style={{ 
        padding: '80px 0', 
        backgroundColor: 'white' 
      }}>
        <div className="container">
          <h2 style={{ 
            textAlign: 'center', 
            fontSize: isMobile ? '2rem' : '3rem', 
            marginBottom: '60px' 
          }}>
            Get Started in 3 Simple Steps
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '40px'
          }}>
            {[
              {
                step: "1",
                title: "Sign Up",
                description: "Tell us about your restaurant and we'll customize your apps"
              },
              {
                step: "2",
                title: "Customize",
                description: "Add your menu, branding, and set up your payment processing"
              },
              {
                step: "3",
                title: "Launch",
                description: "We publish your apps and you start taking orders immediately"
              }
            ].map((step, idx) => (
              <div key={idx} style={{ textAlign: 'center' }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  backgroundColor: '#7c3aed',
                  color: 'white',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  margin: '0 auto 20px'
                }}>
                  {step.step}
                </div>
                <h3 style={{ fontSize: '1.5rem', marginBottom: '10px' }}>{step.title}</h3>
                <p style={{ color: '#6b7280' }}>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section style={{ 
        backgroundColor: '#7c3aed', 
        color: 'white', 
        padding: '80px 0',
        textAlign: 'center'
      }}>
        <div className="container">
          <h2 style={{ 
            fontSize: isMobile ? '2rem' : '3rem', 
            marginBottom: '20px' 
          }}>
            Ready to Grow Your Restaurant?
          </h2>
          <p style={{ 
            fontSize: '1.3rem', 
            marginBottom: '40px',
            opacity: 0.95,
            maxWidth: '600px',
            margin: '0 auto 40px'
          }}>
            Join hundreds of restaurants saving thousands on commission fees
          </p>
          <button 
            onClick={() => setCurrentPage('support')}
            style={{
              backgroundColor: 'white',
              color: '#7c3aed',
              border: 'none',
              padding: '18px 50px',
              borderRadius: '8px',
              fontSize: '1.3rem',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Get Your Free Demo
          </button>
          <p style={{
            marginTop: '20px',
            opacity: 0.9
          }}>
            No credit card required • Setup in minutes
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomePage;